import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { injectFollowUpWording } from '@modules/common/utils/follow-up-wording';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { itemFollowStoreFor, type ItemFollowStore } from '@modules/common/services/item-follow/item-follow-store';
import { ITEM_FOLLOW_SAVED, itemFollowErrorMessage } from '@modules/common/services/item-follow/item-follow-messages';
import { catchError, defaultIfEmpty, forkJoin, of } from 'rxjs';
import { type RailMenuItem } from '@modules/common/components/rail-menu/rail-menu';
import { clearRailFollows } from '@modules/notes/components/sidebar-context-menu/rail-clear-follows';
import { ContactsFilters } from '@modules/contacts/types/contacts-filters';
import { GroupsFilters } from '@modules/contacts/types/groups-filters';
import { type ContactsRailKey, type ContactsRailScope, contactsRowMenu } from './contacts-rail-menu';
import { PURGE_REFUSED_REASON } from '@modules/common/pipes/can-purge.pipe';
// Common
import { Component, inject, Input, type OnInit } from '@angular/core';
import { ContactsTenantScopeService } from '../../services/contacts-tenant-scope.service';

// Utils
import { Like } from '@modules/common/types/like';
import { checkExhaustiveness } from '@modules/common/utils/switch';

// Types
import { Application } from '@modules/common/types/application';
import { Contact } from '@modules/contacts/types/contact';
import { Group } from '@modules/contacts/types/group';

// Services
import { ContactsService } from '@modules/contacts/services/contacts.service';
import { GroupsService } from '@modules/contacts/services/groups.service';

// Components
import { BaseSidebarContextMenuComponent } from '@modules/common/components/base-sidebar-context-menu/base-sidebar-context-menu.component';
import { StitchType } from '@sharedlib/common/types/StitchType';

@Component({
  selector: 'app-sidebar-context-menu',
  templateUrl: './sidebar-context-menu.component.html',
  styleUrls: [
    '../../../common/components/base-sidebar-context-menu/base-sidebar-context-menu.component.less',
    './sidebar-context-menu.component.less',
  ],
  standalone: false,
})
export class SidebarContextMenuComponent
  extends BaseSidebarContextMenuComponent<StitchType.CONTACT, StitchType.GROUP, ContactsRailKey>
  implements OnInit
{
  protected readonly followUpWording = injectFollowUpWording();
  protected readonly PURGE_REFUSED_REASON = PURGE_REFUSED_REASON;

  /** A workspace rail row: the shared rail menu (`contacts-rail-menu.ts`). Other menus keep the template below it. */
  @Input() public rail = false;
  /** Whether the row belongs to the member's My Workspace or to the Team Workspace. */
  @Input() public railScope: ContactsRailScope = 'mine';

  /** "New contact" on a rail row: a plain draft, never the row's marker or the selected group. */
  protected readonly railNewContact = new Contact();
  /** "New contact group" on a rail row: a plain draft, never the row's marker or the selected parent. */
  protected readonly railNewGroup = new Group();
  /** The row's own tenant for the rail's forms and requests, never the selection's. */
  protected railTenantItems: ReadonlyArray<{ accountId: string | undefined }> = [];
  private railMenuCache: { key: string; menu: readonly RailMenuItem[] | undefined } | undefined;

  advancedSearchStateKey = Application.contacts;
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly accountKind = inject(AccountKindService);
  /**
   * Whether the menu ends with a settings entry: "View settings…" on a rail row. No login's Contacts rail offers the
   * old "Settings" page (Pavel 2026-11-04: personal logins in #2971, team logins too — the page saves nothing and the
   * settings window has no equivalent). The dividers before it follow, so a menu never ends with a line.
   */
  protected get endsWithSettings(): boolean {
    return this.rail;
  }
  protected get isTeamRail(): boolean {
    return this.tenantId != null && this.accountKind.isTeam() && this.spaceContextStore.insideTeam();
  }
  private readonly followToaster = inject(ToasterService);
  /** The Following folder lists followed contacts and followed groups, so clearing it clears both. */
  protected clearMySubscriptions(): void {
    const tenant = this.tenantId;
    const stores = [itemFollowStoreFor(this.itemsService), itemFollowStoreFor(this.containerItemsService)].filter(
      (store): store is ItemFollowStore => store !== null,
    );
    if (!tenant || !stores.length) return;
    let refused = false;
    forkJoin(
      stores.map((store) =>
        store.clear(tenant).pipe(
          defaultIfEmpty({ removed: 0 }),
          catchError((error: unknown) => {
            refused = true;
            this.followToaster.show({ text: itemFollowErrorMessage(error) });
            return of({ removed: 0 });
          }),
        ),
      ),
    ).subscribe((answers) => {
      if (refused) return;
      const removed = answers.reduce((total, answer) => total + answer.removed, 0);
      this.followToaster.show({ text: removed > 0 ? ITEM_FOLLOW_SAVED : 'No items in this view.' });
    });
  }
  private readonly contactsScope = inject(ContactsTenantScopeService, { optional: true });

  constructor(
    private readonly contactsService: ContactsService,
    private readonly groupsService: GroupsService,
  ) {
    super(contactsService, groupsService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.railTenantItems = [{ accountId: this.tenantId ?? undefined }];
  }

  /** The rail menu of this row, or `undefined` for a row that keeps the template's own menu. */
  protected get railMenu(): readonly RailMenuItem[] | undefined {
    if (!this.rail) return undefined;
    const key = `${this.sidebarFilterKey}|${this.railScope}`;
    if (this.railMenuCache?.key !== key) {
      this.railMenuCache = { key, menu: contactsRowMenu(this.sidebarFilterKey, this.railScope) };
    }
    return this.railMenuCache.menu;
  }

  /** "View settings…" on this row: the card for the list the row opens. */
  protected openRowViewSettings(): void {
    this.openViewSettings(this.sidebarFilterKey === 'all_groups' ? 'groups' : 'contacts');
  }

  protected runRailEntry(id: string): void {
    const entry = this.railMenu?.find((candidate) => candidate?.id === id);
    // The shared renderer already answers Coming soon and refuses a disabled entry; a direct call must too.
    if (!entry || entry.comingSoon || entry.disabled) return;
    switch (id) {
      case 'settings':
        this.openRowViewSettings();
        return;
      case 'clear':
        if (this.sidebarFilterKey === 'following') void this.clearRailSubscriptions();
        else this.clearRailRow();
        return;
      case 'clear-team':
        this.clearRailRow();
        return;
      case 'empty-trash-all':
        // My Workspace only (the team's entry is Coming soon): today's confirmed purge, unchanged.
        this.emptyTrash();
        return;
    }
  }

  /** Clears only the clicked row's visible contacts and groups in the row's tenant, as of one fixed instant. */
  private clearRailRow(): void {
    const key = this.sidebarFilterKey;
    if (key !== 'flagged' && key !== 'followed' && key !== 'snoozed') return;
    const cutoff = new Date();
    const contacts = new ContactsFilters({ railPostponedTill: cutoff }).applyVirtualFolder(key);
    const groups = new GroupsFilters({ railPostponedTill: cutoff }).applyVirtualFolder(key);
    const config = { tenantItems: this.railTenantItems };
    if (key === 'flagged') {
      this.contactsService.flag(contacts, false, config);
      this.groupsService.flag(groups, false, config);
    } else if (key === 'followed') {
      this.contactsService.followUp(contacts, null, config);
      this.groupsService.followUp(groups, null, config);
    } else {
      this.contactsService.snooze(contacts, null, config);
      this.groupsService.snooze(groups, null, config);
    }
  }

  /**
   * Team Following · Clear my subscriptions. The whole-list DELETE would also clear follows of archived and
   * trashed items the view never shows, so this reads what the view lists first and unfollows those, one by one.
   */
  private async clearRailSubscriptions(): Promise<void> {
    const tenant = this.tenantId;
    if (this.railScope !== 'team' || !tenant || tenant !== this.spaceContextStore.contextId()) return;
    const contacts = this.contactsService;
    const groups = this.groupsService;
    await clearRailFollows(
      tenant,
      this.spaceContextStore,
      [
        {
          list: (offset, limit) => contacts.follow.api.list(tenant, offset, limit),
          unfollow: (id) => contacts.follow.api.unfollow(id, tenant),
          record: (id) => contacts.follow.record(id, tenant, false, false),
          refresh: () => contacts.forceRefresh(),
        },
        {
          list: (offset, limit) => groups.follow.api.list(tenant, offset, limit),
          unfollow: (id) => groups.follow.api.unfollow(id, tenant),
          record: (id) => groups.follow.record(id, tenant, false, false),
          refresh: () => groups.forceRefresh(),
        },
      ],
      this.followToaster,
    );
  }

  protected getCommonParams(): Like<Contact | Group> {
    let params: Like<Contact | Group> = { accountId: this.tenantId };

    switch (this.sidebarFilterKey) {
      case 'following':
      case 'shared':
      case 'search_results':
      case 'all_contacts':
      case 'all_groups':
      case 'frequent':
        break;
      case 'vip':
        params.vip = true;
        break;
      case 'archived':
      case 'snoozed':
      case 'deleted':
      case 'flagged':
      case 'followed':
        params = super.getCommonParams();
        break;
      default:
        checkExhaustiveness(this.sidebarFilterKey);
    }

    return params;
  }

  protected stitchItemFactory(params: Partial<Contact>): Contact {
    const sameArea = this.tenantId === undefined || this.contactsScope?.effectiveTenantId() === this.tenantId;
    return new Contact({ ...params, groupId: sameArea ? this.selectedContainerStitchItemId : undefined });
  }

  protected containerStitchItemFactory(params: Partial<Group>): Group {
    return new Group(params);
  }
}
